/**
 * Client side of hosting the CourtsApp MCP's MCP Apps HTML cards: fetch the
 * card HTML from the widget server, and lock it down with the CSP the card
 * declares in its resource metadata before it runs in a sandboxed iframe.
 */
import { createContext } from "react";

export type McpUiCsp = {
  connectDomains?: string[];
  resourceDomains?: string[];
  frameDomains?: string[];
};

export type McpUiResource = { html: string; mimeType?: string; csp?: McpUiCsp };

/** What a card needs from the widget: where to fetch it, and how to send chat text. */
export type McpAppHost = {
  serverUrl: string;
  sendText: (text: string) => unknown;
};

export const McpAppHostContext = createContext<McpAppHost | null>(null);

const resources = new Map<string, Promise<McpUiResource>>();

export function loadUiResource(serverUrl: string, uri: string): Promise<McpUiResource> {
  const key = `${serverUrl}|${uri}`;
  const hit = resources.get(key);
  if (hit) return hit;
  const p = fetch(`${serverUrl}/api/mcp-ui?uri=${encodeURIComponent(uri)}`).then(async (r) => {
    if (!r.ok) throw new Error(`mcp-ui ${r.status}`);
    const body = (await r.json()) as McpUiResource;
    if (typeof body.html !== "string" || !body.html) throw new Error("mcp-ui: empty card");
    return body;
  });
  p.catch(() => resources.delete(key)); // let a later card retry
  resources.set(key, p);
  return p;
}

const domains = (list?: string[]) =>
  (list ?? []).filter((d) => /^https:\/\/[\w*.-]+(:\d+)?$/.test(d)).join(" ");

/** The card's declared CSP, per the MCP Apps spec (connect vs. resource domains). */
export function buildCsp(csp?: McpUiCsp): string {
  const res = domains(csp?.resourceDomains);
  const connect = domains(csp?.connectDomains);
  const frames = domains(csp?.frameDomains);
  return [
    "default-src 'none'",
    `script-src 'unsafe-inline' 'unsafe-eval' blob: ${res}`,
    `style-src 'unsafe-inline' ${res}`,
    `img-src data: blob: ${res}`,
    `font-src data: ${res}`,
    `media-src data: blob: ${res}`,
    `connect-src ${connect || "'none'"}`,
    `frame-src ${frames || "'none'"}`,
    "base-uri 'none'",
    "form-action 'none'",
  ]
    .map((d) => d.trim())
    .join("; ");
}

/** The card HTML with its CSP as the first thing in <head>. */
export function withCsp(html: string, csp?: McpUiCsp): string {
  const meta = `<meta http-equiv="Content-Security-Policy" content="${buildCsp(csp).replace(/"/g, "&quot;")}">`;
  const head = /<head[^>]*>/i.exec(html);
  if (head) return html.slice(0, head.index + head[0].length) + meta + html.slice(head.index + head[0].length);
  return `<!doctype html><html><head>${meta}</head><body>${html}</body></html>`;
}
